<template>
  <div id="app" :class="{ 'dark-mode': darkMode }">
    <div class="desktop-message">
      This application is designed for mobile devices (phones and tablets) only.
      Please access it from a mobile device for the best experience.
    </div>
    <div v-if="installPrompt" class="install-prompt">
      <p>Install Home Care Manager on your device</p>
      <button @click="installApp">Install</button>
      <button @click="dismissPrompt">Not Now</button>
    </div>
    
    <nav class="mobile-nav" v-if="$route.meta.showNav">
      <router-link to="/dashboard">       
         <font-awesome-icon icon="fas fa-home" class="fa-2x text-warning" />
        <span>Home</span>
      </router-link>
      <!-- <router-link to="/schedule" v-if="hasAdminRoles">      
         <font-awesome-icon icon="fas fa-calendar-alt" class="fa-2x text-warning" />
        <span>Schedule</span>
      </router-link> -->
      <!-- <router-link to="/patients" v-if="hasAdminRoles">
        <font-awesome-icon icon="fas fa-users" class="fa-2x text-warning" />
        <span>Patients</span>
      </router-link> -->
      <!-- <router-link to="/medications" v-if="hasAdminRoles || hasRoleAsNurse">       
         <font-awesome-icon icon="fas fa-pills" class="fa-2x text-warning" />
        <span>Meds</span>
      </router-link> -->
      <router-link to="/profile">
        <font-awesome-icon icon="fas fa-user" class="fa-2x text-warning" />
        <span>Profile</span>
      </router-link>
      <router-link to="/login" @click.prevent="logout">
         <font-awesome-icon icon="fas fa-power-off" class="fa-2x text-danger" />
        <span>Logout</span>
      </router-link>
    </nav>
    
    <main :class="{'has-bottom-nav': $route.meta.showNav}">
      <router-view />
    </main>
    
    <!-- <div class="connection-status" :class="{ offline: !isOnline }">
      {{ isOnline ? 'Online' : 'Offline - Working locally' }}
    </div> -->
  </div>
</template>

<script>
import { useAuthStore } from '/src/stores/auth.js'

export default {
  name: 'App',
  data() {
    return {
      darkMode: false,
      installPrompt: null,
      isOnline: navigator.onLine,
      deferredPrompt: null,
      idleTimeout: null,
      idleTime: 30 * 60 * 1000 // 30 minutes
    }
  },
  computed: {
    authStore() {
      return useAuthStore()
    },
     hasAdminRoles(){
        const authStore = this.authStore
        const hasRole = authStore?.hasAnyRole
        ? authStore.hasAnyRole(['Administrator', 'Admin'])
        : false
        return hasRole
    },
    hasRoleAsNurse(){
        const authStore = this.authStore
        const hasRole = authStore?.hasAnyRole
        ? authStore.hasAnyRole(['Nurse'])
        : false
        return hasRole
    },
  },
  watch: {
    'authStore.isAuthenticated'(newVal) {
      if (newVal) {
        this.startIdleTimer()
      } else {
        this.clearIdleTimer()
      }
    }
  },
  async mounted() {
    if (this.authStore.isAuthenticated && this.authStore.fetchUser) {
      await this.authStore.fetchUser()
    }
    if (this.authStore.isAuthenticated) {
      this.startIdleTimer()
    }
  },
  beforeUnmount() {
    window.removeEventListener('preferences-updated', this.handlePreferencesUpdate)
    this.clearIdleTimer()
    window.removeEventListener('mousedown', this.resetIdleTimer)
    window.removeEventListener('mousemove', this.resetIdleTimer)
    window.removeEventListener('keypress', this.resetIdleTimer)
    window.removeEventListener('scroll', this.resetIdleTimer)
    window.removeEventListener('touchstart', this.resetIdleTimer)
  },
  created() {
    this.loadPreferences()
    window.addEventListener('preferences-updated', this.handlePreferencesUpdate)

    // Listen for beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      this.deferredPrompt = e;
      this.showInstallPrompt();
    });
    
    // Online/offline detection
    window.addEventListener('online', () => this.isOnline = true);
    window.addEventListener('offline', () => this.isOnline = false);

    // Idle timeout events
    window.addEventListener('mousedown', this.resetIdleTimer)
    window.addEventListener('mousemove', this.resetIdleTimer)
    window.addEventListener('keypress', this.resetIdleTimer)
    window.addEventListener('scroll', this.resetIdleTimer)
    window.addEventListener('touchstart', this.resetIdleTimer)
  },
  methods: {
    loadPreferences() {
      try {
        const raw = localStorage.getItem('userPreferences')
        const prefs = raw ? JSON.parse(raw) : null
        this.darkMode = Boolean(prefs?.darkMode)
      } catch (error) {
        this.darkMode = false
      }
    },
    handlePreferencesUpdate(event) {
      this.darkMode = Boolean(event?.detail?.darkMode)
    },
    logout() {
      if (this.authStore?.clearAuth) {
        this.authStore.clearAuth()
      }
      this.$router.push('/login')
    },
    showInstallPrompt() {
      if (localStorage.getItem('installPromptDismissed') !== 'true') {
        this.installPrompt = true;
      }
    },
    async installApp() {
      if (this.deferredPrompt) {
        this.deferredPrompt.prompt();
        const { outcome } = await this.deferredPrompt.userChoice;
        if (outcome === 'accepted') {
          console.log('App installed');
        }
        this.deferredPrompt = null;
      }
      this.installPrompt = false;
    },
    dismissPrompt() {
      this.installPrompt = false;
      localStorage.setItem('installPromptDismissed', 'true');
    },
    startIdleTimer() {
      this.clearIdleTimer()
      this.idleTimeout = setTimeout(() => {
        this.logout()
      }, this.idleTime)
    },
    clearIdleTimer() {
      if (this.idleTimeout) {
        clearTimeout(this.idleTimeout)
        this.idleTimeout = null
      }
    },
    resetIdleTimer() {
      if (this.authStore.isAuthenticated) {
        this.startIdleTimer()
      }
    }
  }
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

#app {
  font-family: Gotham, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #ffffff;
  max-width: 768px;
  margin: 0 auto;
  background: #e7dec9;
  min-height: 100vh;
}

#app.dark-mode {
  background: #1f1b16;
  color: #f3ede5;
}

#app.dark-mode .profile-page,
#app.dark-mode .dashboard,
#app.dark-mode .view-history-container,
#app.dark-mode .vitals-container,
#app.dark-mode .history-container {
  background: #1f1b16;
}

#app.dark-mode .card,
#app.dark-mode .history-card,
#app.dark-mode .vitals-card,
#app.dark-mode .stat-card,
#app.dark-mode .appointment-card,
#app.dark-mode .history-record {
  background: #2a241d;
  border-color: rgba(202, 127, 92, 0.2);
  color: #f3ede5;
}

#app.dark-mode .card-header,
#app.dark-mode .card-body,
#app.dark-mode .record-section p {
  background: transparent;
  color: inherit;
}

#app.dark-mode .text-muted,
#app.dark-mode .page-info,
#app.dark-mode .record-date {
  color: #c7b9ad;
}

#app.dark-mode .form-control,
#app.dark-mode .form-select,
#app.dark-mode textarea,
#app.dark-mode input {
  background: #1f1b16;
  color: #f3ede5;
  border-color: rgba(202, 127, 92, 0.35);
}

#app.dark-mode .form-control:disabled,
#app.dark-mode .form-select:disabled {
  background: #201b15;
  color: #b9aca1;
}

#app.dark-mode .form-control::placeholder {
  color: #9f9084;
}

#app.dark-mode .btn-outline-primary {
  color: #f3ede5;
  border-color: rgba(202, 127, 92, 0.6);
}

#app.dark-mode .btn-outline-primary:hover {
  background: rgba(202, 127, 92, 0.2);
}

#app.dark-mode .action-label {
  color: #d2c4b8;
}

#app.dark-mode .mobile-nav {
  background: #0b1e3d;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.3);
}

#app.dark-mode .mobile-nav a {
  color: #f3ede5;
}

#app.dark-mode .mobile-nav a.router-link-exact-active {
  color: #f3d3c4;
}

#app.dark-mode .install-prompt {
  background: #5a3a2c;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
}

#app.dark-mode .install-prompt button {
  background: #ca7f5c;
}

#app.dark-mode .connection-status {
  background: #5a3a2c;
}

.mobile-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #0b1e3d;
  display: flex;
  justify-content: space-around;
  padding: 10px 0;
  box-shadow: 0 -2px 10px rgba(0,0,0,0.1);
  z-index: 1000;  
}

.mobile-nav a {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-decoration: none;
  color: #ffffff;
  font-size: 12px;
  font-weight: 900;
}

.mobile-nav a.router-link-exact-active {
  color: #ffffff;
}

.mobile-nav i {
  font-size: 20px;
  margin-bottom: 4px;
}

main.has-bottom-nav {
  padding-bottom: 70px;
}

.install-prompt {
  position: fixed;
  bottom: 80px;
  left: 20px;
  right: 20px;
  background: #ca7f5c;
  padding: 15px;
  border-radius: 10px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
  z-index: 1001;
  text-align: center;
}

.install-prompt button {
  margin: 5px;
  padding: 8px 20px;
  border: none;
  border-radius: 5px;
  background: #ca7f5c;
  color: white;
  cursor: pointer;
}

.connection-status {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: #ca7f5c;
  color: white;
  text-align: center;
  padding: 5px;
  font-size: 14px;
}

.connection-status.offline {
  background: #ca7f5c;
}

.desktop-message {
  display: none;
}

@media (min-width: 1025px) {
  .desktop-message {
    display: flex;
    position: fixed;
    inset: 0;
    background: #ffffff;
    z-index: 9999;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    text-align: center;
    padding: 24px;
    color: #333333;
    line-height: 1.4;
  }

  main,
  .mobile-nav,
  .install-prompt,
  .connection-status {
    display: none;
  }
}

#app.dark-mode .desktop-message {
  background: #1f1b16;
  color: #f3ede5;
}
</style>
